import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Lock, Eye, EyeOff } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { T } from '@/utils/translations';
import { fetchRecommendations, fetchRecommendationStats, fetchRecommendationHistory, fetchRecommendationPerformance } from '@/utils/api';
import Sidebar from '@/components/app/Sidebar';
import AppHeader from '@/components/app/AppHeader';
import StatCard from '@/components/common/StatCard';
import RecoCard from '@/components/app/RecoCard';
import HistoryView from '@/components/app/HistoryView';
import PerformanceView from '@/components/app/PerformanceView';
import AlertsView from '@/components/app/AlertsView';
import SettingsView from '@/components/app/SettingsView';
import Onboarding from '@/components/onboarding/Onboarding';

// Critères Kamach propriétaires (ALGORITHME CONFIDENTIEL)
const KAMACH_CRITERIA = {
  pe_ratio: { max: 15, label: "P/E Ratio", desc: "≤ 15" },
  current_ratio: { min: 2, label: "Current Ratio", desc: "≥ 2.0" },
  debt_equity: { max: 0.5, label: "Debt/Equity", desc: "≤ 0.5" },
  dividend_yield: { min: 2, label: "Dividend Yield", desc: "≥ 2%" },
  market_cap: { min: 2000, label: "Market Cap", desc: "≥ $2B" },
  working_capital: { positive: true, label: "Working Capital", desc: "> 0" },
  earnings_stability: { years: 10, label: "Earnings Stability", desc: "10 ans positifs" },
  dividend_record: { years: 20, label: "Dividend Record", desc: "20 ans consécutifs" },
  earnings_growth: { min: 33, label: "EPS Growth", desc: "≥ 33% sur 10 ans" },
  price_to_assets: { max: 1.5, label: "Price/Book", desc: "≤ 1.5x" }
};

const MainApp = () => {
  const navigate = useNavigate();
  const { lang } = useApp();
  const t = T[lang] || T.fr;
  const [tab, setTab] = useState('all');
  const [view, setView] = useState('active');
  const [showOnboarding, setShowOnboarding] = useState(() => !localStorage.getItem('axiom_onboarded'));
  const [recos, setRecos] = useState([]);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Mode administrateur caché (5 clics sur le logo)
  const [adminMode, setAdminMode] = useState(false);
  const [logoClicks, setLogoClicks] = useState(0);
  const [showCriteria, setShowCriteria] = useState(false);

  const handleLogoClick = () => {
    const newClicks = logoClicks + 1;
    setLogoClicks(newClicks);
    
    if (newClicks >= 5) {
      setAdminMode(!adminMode);
      setLogoClicks(0);
      // Notification discrète
      console.log(adminMode ? '🔒 Mode admin désactivé' : '🔓 Mode admin activé');
    }
    
    // Reset après 2 secondes d'inactivité
    setTimeout(() => setLogoClicks(0), 2000);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [recosData, statsData, historyData, perfData] = await Promise.all([
        fetchRecommendations(),
        fetchRecommendationStats(),
        fetchRecommendationHistory(),
        fetchRecommendationPerformance()
      ]);
      setRecos(recosData);
      setStats(statsData);
      setHistory(historyData);
      setPerformance(perfData);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = tab === 'all' ? recos : recos.filter(r => r.asset === tab);

  const dashboardStats = stats ? [
    { label: t.app.activeRecos, value: stats.active_recos, change: '+2', up: true },
    { label: `${t.app.winRate} (30j)`, value: stats.win_rate_30d, change: '+5%', up: true },
    { label: t.app.avgReturn, value: stats.avg_return, change: null },
    { label: 'R:R moyen', value: stats.risk_reward, change: null }
  ] : [];

  if (showOnboarding) {
    return <Onboarding onComplete={() => setShowOnboarding(false)} />;
  }

  return (
    <div className="app-layout">
      <Sidebar 
        view={view} 
        setView={setView} 
        recoCount={recos.length} 
        t={t} 
        onLogoClick={handleLogoClick}
        adminMode={adminMode}
      />

      <main className="main-content">
        <AppHeader title={t.app.recommendations} adminMode={adminMode} />

        <div className="content-area">
          {/* PANNEAU ADMIN - Critères Kamach (visible uniquement en mode admin) */}
          {adminMode && (
            <div style={{
              marginBottom: 24,
              padding: 20,
              background: 'linear-gradient(135deg, rgba(244, 208, 63, 0.1) 0%, rgba(245, 87, 108, 0.1) 100%)',
              border: '2px solid rgba(244, 208, 63, 0.3)',
              borderRadius: 16,
              position: 'relative'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Lock size={20} style={{ color: 'var(--accent-gold)' }} />
                  <span style={{ fontWeight: 700, color: 'var(--accent-gold)', fontSize: '0.875rem', letterSpacing: '0.1em' }}>
                    MODE ADMINISTRATEUR — ALGORITHME PROPRIÉTAIRE
                  </span>
                </div>
                <button 
                  onClick={() => setShowCriteria(!showCriteria)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '8px 16px', background: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-color)', borderRadius: 8,
                    color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.8125rem'
                  }}
                >
                  {showCriteria ? <EyeOff size={14} /> : <Eye size={14} />}
                  {showCriteria ? 'Masquer critères' : 'Voir critères'}
                </button>
              </div>
              
              {showCriteria && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(5, 1fr)',
                  gap: 12,
                  padding: 16,
                  background: 'var(--bg-secondary)',
                  borderRadius: 12,
                  border: '1px solid var(--border-color)'
                }}>
                  {Object.entries(KAMACH_CRITERIA).map(([key, criteria]) => (
                    <div key={key} style={{
                      padding: 12,
                      background: 'var(--bg-tertiary)',
                      borderRadius: 8,
                      borderLeft: '3px solid var(--accent-gold)'
                    }}>
                      <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                        {criteria.label}
                      </div>
                      <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--accent-gold)' }}>
                        {criteria.desc}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div style={{ marginTop: 12, fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                ⚠️ Ces critères sont confidentiels et ne sont pas visibles par les abonnés. Basés sur la méthodologie Yvon Kamach.
              </div>
            </div>
          )}

          {loading && (
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-tertiary)' }}>
              <Sparkles size={32} style={{ marginBottom: 16, animation: 'pulse 1.5s infinite' }} />
              <p>{lang === 'fr' ? 'Chargement des recommandations...' : 'Loading recommendations...'}</p>
            </div>
          )}

          {!loading && view === 'active' && (
            <>
              <div className="stats-row">
                {dashboardStats.map((s, i) => (
                  <StatCard key={i} {...s} />
                ))}
              </div>

              <div className="tabs">
                <button className={`tab ${tab === 'all' ? 'active' : ''}`} onClick={() => setTab('all')}>{t.app.all}</button>
                <button className={`tab ${tab === 'stocks' ? 'active' : ''}`} onClick={() => setTab('stocks')}>📈 {t.app.stocks}</button>
                <button className={`tab ${tab === 'crypto' ? 'active' : ''}`} onClick={() => setTab('crypto')}>₿ {t.app.crypto}</button>
              </div>

              <div className="reco-grid">
                {filtered.map(r => <RecoCard key={r.id} reco={r} t={t} />)}
              </div>
            </>
          )}

          {!loading && view === 'history' && <HistoryView history={history} t={t} />}
          {!loading && view === 'performance' && <PerformanceView performance={performance} t={t} lang={lang} />}
          {!loading && view === 'alerts' && <AlertsView t={t} lang={lang} />}
          {!loading && view === 'settings' && <SettingsView t={t} lang={lang} />}
        </div>

        <div className="disclaimer-bar">
          {lang === 'fr' 
            ? "AX-IOM fournit des recommandations générales et ne constitue pas un conseil en investissement personnalisé. Les performances passées ne préjugent pas des performances futures."
            : 'AX-IOM provides general recommendations and does not constitute personalized investment advice. Past performance does not guarantee future results.'
          }
        </div>
      </main>
    </div>
  );
};

export default MainApp;
